import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import { useNotificationActions } from './useNotificationActions';
import { Notification } from '../types/notification';

/**
 * Custom hook for managing notification menu state and actions
 * Provides a unified interface for notification menu interactions
 */
export function useNotificationMenu() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  
  // Get notifications data from context
  const { 
    notifications, 
    unreadCount
  } = useNotifications();
  
  // Get optimistic actions from the actions hook
  const { 
    isLoading,
    markAsRead, 
    markAllAsRead,
    clearNotification 
  } = useNotificationActions();

  /**
   * Handles opening the notification menu
   */
  const handleOpenMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  /**
   * Handles closing the notification menu
   */
  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  /**
   * Handles clicking on a notification
   * - Marks notification as read if unread
   * - Navigates to the linked page if available
   * - Closes the menu
   */
  const handleNotificationClick = useCallback(async (notification: Notification) => {
    try {
      if (!notification.read) {
        await markAsRead(notification.id);
      }
      
      handleCloseMenu();
      
      // Check for actionUrl (from backend) or link (legacy)
      const targetUrl = (notification as any).actionUrl || notification.link;
      if (targetUrl && targetUrl !== '/notifications') {
        navigate(targetUrl);
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
      // Error is already handled by useNotificationActions with a snackbar
    }
  }, [markAsRead, navigate, handleCloseMenu]);

  /**
   * Handles marking all notifications as read
   */
  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await markAllAsRead();
      handleCloseMenu();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      // Error is already handled by useNotificationActions with a snackbar
    }
  }, [markAllAsRead, handleCloseMenu]);

  /**
   * Handles clearing a notification
   */
  const handleClearNotification = useCallback(async (id: string) => {
    try {
      await clearNotification(id);
    } catch (error) {
      console.error('Error clearing notification:', error);
      // Error is already handled by useNotificationActions with a snackbar
    }
  }, [clearNotification]);

  /**
   * Handles navigating to the notifications page
   */
  const handleViewAll = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    navigate('/notifications');
    handleCloseMenu();
  }, [navigate, handleCloseMenu]);

  return {
    // State
    anchorEl,
    isMenuOpen: Boolean(anchorEl),
    notifications,
    unreadCount,
    isLoading,

    // Handlers
    handleOpenMenu,
    handleCloseMenu,
    handleNotificationClick,
    handleMarkAllAsRead,
    handleClearNotification,
    handleViewAll,
    
    // For backward compatibility with components using the direct method
    clearNotification: handleClearNotification
  };
}

// Type export for better type inference
export type UseNotificationMenuReturn = ReturnType<typeof useNotificationMenu>;
